import { moment } from '@eventbrite/date';
import isEmpty from 'lodash/isEmpty';
import { Dispatch } from 'redux';
import { OrderResponse } from '../api/models/order';
import {
    CannedQuestionName,
    InteractiveQuestion,
    QuestionsCollection,
    QuestionsResponses,
    QuestionType,
} from '../api/models/survey';
import { getSurveyResponsesByAttendeeIdFromOrderResponse } from '../api/transformations/order';
import {
    getInteractiveQuestionsByAttendeeIdFromOrderResponse,
    getQuestionsCollectionFromOrderResponse,
} from '../api/transformations/survey';
import { ORGANIZER_QUESTIONS_FORM_NAME } from '../constants';
import { CHECKOUT_FORM_NAME } from '../constants/checkoutForm';
import {
    BUYER_EMAIL_FIELD,
    BUYER_FIRST_NAME_FIELD,
    BUYER_LAST_NAME_FIELD,
    CANNED_QUESTION_EMAIL_ADDRESS,
    CANNED_QUESTION_FIRST_NAME,
    CANNED_QUESTION_LAST_NAME,
    getFieldName,
} from '../containers/checkout/constants';
import {
    FormAnswer,
    PrefillSurveyFieldsInfo,
    SurveyQuestion,
} from '../models/survey';
import {
    getFormName,
    getFormValuesWithoutSyncValidationErroredFields,
    getPrefillFirstAttendeeInfo,
    getPrefillSurveyInfo,
} from '../selectors/forms';
import {
    getIsCurrentPageDisplayingBasicInfoQuestions,
    getSurvey,
} from '../selectors/survey';
import { CheckoutState } from '../state';
import {
    areMultipleResponsesSelected,
    changeSurveyField,
    getChoiceIdByText,
    getMultipleChoicesSelected,
    transformAddressFromApi,
} from '../utils/surveys';
import { prefillAttendeeBasicInfoFields } from './prefillAttendeeBasicInfoFields';
import { submitOptionalOrganizerQuestionsForm } from './submitOptionalOrganizerQuestionsForm';

export enum SurveyActionType {
    initSurveys = 'initSurveys',
}

export interface InitSurveysAction {
    type: SurveyActionType.initSurveys;
    payload: {
        questionsCollection: QuestionsCollection;
        interactiveQuestionsByAttendeeId: Record<
            string,
            Array<InteractiveQuestion>
        >;
        surveyResponsesByAttendeeId: Record<string, QuestionsResponses>;
    };
}

export const getInitSurveysAction = (payload: {
    questionsCollection: QuestionsCollection;
    interactiveQuestionsByAttendeeId: Record<
        string,
        Array<InteractiveQuestion>
    >;
    surveyResponsesByAttendeeId: Record<string, QuestionsResponses>;
}): InitSurveysAction => ({
    type: SurveyActionType.initSurveys,
    payload,
});

export const getInitSurveysActionFromOrderResponse = (
    orderResponse: OrderResponse,
): InitSurveysAction =>
    getInitSurveysAction({
        questionsCollection:
            getQuestionsCollectionFromOrderResponse(orderResponse) ?? {},
        interactiveQuestionsByAttendeeId:
            getInteractiveQuestionsByAttendeeIdFromOrderResponse(
                orderResponse,
            ) ?? {},
        surveyResponsesByAttendeeId:
            getSurveyResponsesByAttendeeIdFromOrderResponse(orderResponse),
    });

export type SurveyAction = InitSurveysAction;

export const prefillSurveyFields =
    ({
        formName = CHECKOUT_FORM_NAME,
        emailToPrefill = '',
        firstNameToPrefill = '',
        lastNameToPrefill = '',
        attendeeWithSurvey,
        isTransferredOrder,
        shouldManageUserSavedSurveyData,
        shouldPrefillFirstAttendeeBasicInfoQuestions,
        shouldCopyBuyerInformation,
        surveyReponsesByAttendeeId = {},
    }: PrefillSurveyFieldsInfo) =>
    (dispatch: Dispatch<any>) => {
        if (emailToPrefill) {
            dispatch(
                changeSurveyField(BUYER_EMAIL_FIELD, emailToPrefill, formName),
            );
        }

        if (!shouldManageUserSavedSurveyData) {
            return;
        }

        if (firstNameToPrefill) {
            dispatch(
                changeSurveyField(
                    BUYER_FIRST_NAME_FIELD,
                    firstNameToPrefill,
                    formName,
                ),
            );
        }

        if (lastNameToPrefill) {
            dispatch(
                changeSurveyField(
                    BUYER_LAST_NAME_FIELD,
                    lastNameToPrefill,
                    formName,
                ),
            );
        }

        if (isTransferredOrder && shouldCopyBuyerInformation) {
            const prefillMappings = [
                {
                    value: firstNameToPrefill,
                    field: CannedQuestionName.firstName,
                },
                {
                    value: lastNameToPrefill,
                    field: CannedQuestionName.lastName,
                },
                {
                    value: emailToPrefill,
                    field: CannedQuestionName.email,
                },
            ];

            for (const attendeeId in surveyReponsesByAttendeeId) {
                const surveyResponses = surveyReponsesByAttendeeId[attendeeId];
                prefillMappings.forEach(({ value, field }) => {
                    if (value && !surveyResponses[field]) {
                        surveyResponses[field] = value;
                    }
                });
            }
        }

        if (isTransferredOrder) {
            for (const [attendeeId, surveyWithResponses] of Object.entries(
                surveyReponsesByAttendeeId,
            )) {
                for (const [questionName, surveyFieldValue] of Object.entries(
                    surveyWithResponses,
                )) {
                    const surveyFieldName = getFieldName(
                        attendeeId,
                        questionName,
                    );

                    dispatch(
                        changeSurveyField(
                            surveyFieldName,
                            surveyFieldValue,
                            formName,
                        ),
                    );
                }
            }
        } else if (
            shouldPrefillFirstAttendeeBasicInfoQuestions &&
            attendeeWithSurvey
        ) {
            dispatch(
                prefillAttendeeBasicInfoFields({
                    attendeeId: attendeeWithSurvey.id,
                    emailToPrefill,
                    firstNameToPrefill,
                    lastNameToPrefill,
                    formName,
                }),
            );
        }
    };

export const prefillFirstAttendeeSurvey =
    ({
        formName,
        emailToPrefill,
        firstNameToPrefill,
        lastNameToPrefill,
        attendeeWithSurvey,
    }: PrefillSurveyFieldsInfo) =>
    (dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        if (
            getIsCurrentPageDisplayingBasicInfoQuestions(getState()) &&
            attendeeWithSurvey
        ) {
            dispatch(
                prefillAttendeeBasicInfoFields({
                    attendeeId: attendeeWithSurvey.id,
                    emailToPrefill,
                    firstNameToPrefill,
                    lastNameToPrefill,
                    formName,
                }),
            );
        }
    };

export const getSurveyInfoAndPrefillFieldsAction =
    (formName?: string) =>
    (dispatch: Dispatch<any>, getState: () => CheckoutState) => {
        const state = getState();
        const prefillSurveyFieldsInfo = getPrefillSurveyInfo(state);

        dispatch(prefillSurveyFields({ ...prefillSurveyFieldsInfo, formName }));
    };

export const getSurveyInfoAndPrefillFirstAttendeeAction =
    (formName: string) =>
    (dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        const state = getState();
        const prefillFirstAttendeeInfo = {
            ...getPrefillFirstAttendeeInfo(state),
            formName,
        };

        dispatch(prefillFirstAttendeeSurvey(prefillFirstAttendeeInfo));
    };

export const handleOrganizerQuestionsPageSkip =
    () =>
    async (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
    ) => {
        const state = getState();

        // When pressing the Organizer Questions page > Skip button, we will bypass the
        // validation process by removing the errored fields from the form data to be submitted
        const formDataWithoutValidationErroredFields =
            getFormValuesWithoutSyncValidationErroredFields(
                state,
                ORGANIZER_QUESTIONS_FORM_NAME,
            );

        if (isEmpty(formDataWithoutValidationErroredFields)) {
            return;
        }

        await dispatch(
            submitOptionalOrganizerQuestionsForm(
                formDataWithoutValidationErroredFields,
                true,
            ),
        );
    };

interface PrefillMethodProps {
    answer: FormAnswer;
    attendeeId: string;
    dispatch: Dispatch<any>;
    formName: string;
    question: SurveyQuestion;
}

const prefillDateQuestion = ({
    answer,
    attendeeId,
    dispatch,
    formName,
    question,
}: PrefillMethodProps) => {
    const date = moment(answer, 'MM/DD/YYYY');
    const namespace = `${attendeeId}.${question.name}`;

    dispatch(
        changeSurveyField(
            getFieldName(namespace, 'day'),
            date.date().toString(),
            formName,
        ),
    );

    dispatch(
        changeSurveyField(
            getFieldName(namespace, 'month'),
            (date.month() + 1).toString(),
            formName,
        ),
    );

    dispatch(
        changeSurveyField(
            getFieldName(namespace, 'year'),
            date.year().toString(),
            formName,
        ),
    );
};

const prefillAddressQuestion = ({
    answer,
    attendeeId,
    dispatch,
    formName,
    question,
}: PrefillMethodProps) => {
    const addressFieldsResponses = transformAddressFromApi(
        answer as Record<string, string>,
    );

    for (const response of Object.keys(addressFieldsResponses)) {
        dispatch(
            changeSurveyField(
                getFieldName(`${attendeeId}.${question.name}`, response),
                addressFieldsResponses[response],
                formName,
            ),
        );
    }
};

const prefillCheckboxQuestion = ({
    answer,
    attendeeId,
    dispatch,
    formName,
    question,
}: PrefillMethodProps) => {
    const selectedChoices = !areMultipleResponsesSelected(answer as string)
        ? [getChoiceIdByText({ question, answer })]
        : getMultipleChoicesSelected({ question, answer: answer as string });

    dispatch(
        changeSurveyField(
            getFieldName(attendeeId, question.name),
            selectedChoices as unknown as string,
            formName,
        ),
    );
};

const prefillQuestionWithChoices = ({
    answer,
    attendeeId,
    dispatch,
    formName,
    question,
}: PrefillMethodProps) => {
    const choice = getChoiceIdByText({ question, answer });

    dispatch(
        changeSurveyField(
            getFieldName(attendeeId, question.name),
            choice as string,
            formName,
        ),
    );
};

const prefillPlainTextQuestion = ({
    dispatch,
    question,
    answer,
    attendeeId,
    formName,
}: PrefillMethodProps) => {
    dispatch(
        changeSurveyField(
            getFieldName(attendeeId, question.name),
            answer as string,
            formName,
        ),
    );
};

const prefillQuestionHandlerByQuestionType: Record<
    QuestionType,
    (props: PrefillMethodProps) => void
> = {
    [QuestionType.address]: prefillAddressQuestion,
    [QuestionType.checkbox]: prefillCheckboxQuestion,
    [QuestionType.date]: prefillDateQuestion,
    [QuestionType.dropdown]: prefillQuestionWithChoices,
    [QuestionType.email]: prefillPlainTextQuestion,
    [QuestionType.number]: prefillPlainTextQuestion,
    [QuestionType.paragraph]: prefillPlainTextQuestion,
    [QuestionType.phone]: prefillPlainTextQuestion,
    [QuestionType.radio]: prefillQuestionWithChoices,
    [QuestionType.text]: prefillPlainTextQuestion,
    [QuestionType.url]: prefillPlainTextQuestion,
    [QuestionType.waiver]: prefillPlainTextQuestion,
};

export const prefillPreTicketsOrganizerQuestionsForm =
    () => (dispatch: Dispatch<any>, getState: () => CheckoutState) => {
        const state = getState();

        const {
            preTicketsSurveySettings: { getOriginalAnswers },
        } = getSurvey(state);
        const originalAnswers = getOriginalAnswers(state);

        for (const { attendeeId, answer, question } of originalAnswers) {
            const prefillQuestionMethod =
                prefillQuestionHandlerByQuestionType[question.type];

            prefillQuestionMethod({
                dispatch,
                question,
                answer,
                attendeeId,
                formName: ORGANIZER_QUESTIONS_FORM_NAME,
            });
        }
    };

export const updateFirstAttendeeBasicInfo =
    (copyValuesFromFirstAttendee: boolean) =>
    (dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        const fields = getPrefillFirstAttendeeInfo(getState());
        const attendeeId = fields.attendeeWithSurvey?.id || '';
        const formName = getFormName(getState());

        if (copyValuesFromFirstAttendee) {
            const { emailToPrefill, firstNameToPrefill, lastNameToPrefill } =
                fields;
            dispatch(
                prefillAttendeeBasicInfoFields({
                    formName,
                    emailToPrefill,
                    firstNameToPrefill,
                    lastNameToPrefill,
                    attendeeId,
                }),
            );
        } else {
            [
                CANNED_QUESTION_EMAIL_ADDRESS,
                CANNED_QUESTION_FIRST_NAME,
                CANNED_QUESTION_LAST_NAME,
            ].map((questionName) => {
                dispatch(
                    changeSurveyField(
                        getFieldName(attendeeId, questionName),
                        '',
                        formName,
                    ),
                );
            });
        }
    };
